import { lsGet } from '@/helpers/localStorageHelper';
import i18n from '@/plugins/i18n';
import { useAppStore } from '@/stores/app';
import { useRoleStore } from '@/stores/role';
import { ref } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import navItems from '@/router/navItems';
import { canOpenContentTeamStore } from '@/helpers/contentTeamPermissions';
const pages = import.meta.glob('@/pages/**/*.vue');

const { t } = i18n.global;

const userData = ref({});
const appStore = ref();
const roleStore = ref();

const routes = Object.keys(pages).map(file => {
  // Extract clean name
  const name = file
    .replace(/^.*\/pages\//, '') // remove leading path
    .replace(/\.vue$/, '') // remove extension
    .replace(/\//g, '-') // slashes to dashes
    .replace(/\[\.\.\.(.+?)\]/g, '$1-catchall') // [...id] → id-catchall
    .replace(/\[(.+?)\]/g, '$1'); // [id] → id

  // Build route path
  const path = file
    .replace(/^.*\/pages/, '') // remove leading path
    .replace(/\.vue$/, '') // remove extension
    .replace(/\/index$/, '/') // /about/index.vue → /about/
    // Catch-all dynamic: [...id] → :id(.*)*
    .replace(/\[\.\.\.(.+?)\]/g, ':$1(.*)*')
    // Normal dynamic: [id] → :id
    .replace(/\[(.+?)\]/g, ':$1');

  return {
    path: path === '/index' ? '/' : path,
    name,
    component: pages[file],
    meta: { title: name },
  };
});

const router = createRouter({
  history: createWebHistory(),
  routes,
});

function getTitle(title) {
  return t(title.replace('-id-catchall', '-details').replace(/-([a-z])/g, (_, char) => char.toUpperCase()));
}

const isPathValid = (to, navItem) => {
  let toPath = to.split('/')[1];
  let navPath = navItem.to;
  if (toPath == 'create-order' || toPath == 'pos') {
    toPath = 'order';
  }
  if (toPath == 'contactus') {
    toPath = 'contact-us';
  }

  return navItem.to && (toPath == navPath || toPath + 's' == navPath) && roleStore.value.checkNavPerm(navItem);
};

const checkPathIfExits = (path, nav_item) => {
  let valid_nav_item = false;

  if (Array.isArray(nav_item)) {
    let validRoute = nav_item.filter(navItem => {
      return checkPathIfExits(path, navItem.children ? navItem.children : navItem);
    });

    valid_nav_item = validRoute.length ? validRoute[validRoute.length - 1] : false;
  } else if (isPathValid(path, nav_item)) {
    valid_nav_item = nav_item;
  }

  if (valid_nav_item) {
    let toPath = path.split('/');
    let backPath = '/' + (valid_nav_item.to || '');

    let breadcrumbs = [
      { title: getTitle('dashboard'), disabled: false, href: '/' },
      { title: getTitle(valid_nav_item.title), disabled: toPath.length < 3, href: backPath },
    ];

    if (toPath.length > 2) {
      breadcrumbs.push({
        title: getTitle(toPath[1]) + ' #' + (toPath[2] || '0'),
        disabled: true,
      });
    }

    appStore.value.setBreadcrumbs(breadcrumbs);
  }

  return valid_nav_item ? valid_nav_item.title : false;
};

router.beforeEach(async (to, from) => {
  userData.value = lsGet('userData', true) || null;
  roleStore.value = useRoleStore();
  appStore.value = useAppStore();

  let title = to.path == '/404' ? '404' : to.path == '/login' ? 'login' : 'dashboard';
  if (to.path != '/' && ['/login', '/forgot-password', '/reset-password', '/404', '/not-found'].indexOf(to.path) == -1) {
    title = checkPathIfExits(to.path, navItems);
    if (!title) {
      // The Content Team store details page lives on its own route
      // (/content-team-store/:adminId/:id) and is not part of navItems, so it is
      // gated here: content_team.get to access, plus content_team.show_from_other_admins
      // when the store belongs to a different admin than the logged-in one.
      const [, , adminId, storeId] = to.path.split('/');
      const isContentTeamStore =
        /^\/content-team-store\//.test(to.path) &&
        canOpenContentTeamStore(roleStore.value, adminId, userData.value?.id);
      if (isContentTeamStore) {
        title = 'storeDetails';
        // Build the breadcrumb manually since this route is not in navItems:
        // Dashboard > Content Team > Content Team #adminId > Store #storeId
        appStore.value.setBreadcrumbs([
          { title: getTitle('dashboard'), disabled: false, href: '/' },
          { title: getTitle('content-team'), disabled: false, href: '/content-team' },
          { title: getTitle('content-team') + ' #' + (adminId || '0'), disabled: false, href: '/content-team/' + adminId },
          { title: getTitle('store') + ' #' + (storeId || '0'), disabled: true },
        ]);
      } else {
        router.push({ name: 'not-found' });
      }
    }
  }
  if (to.path == '/pos') {
    title = 'pos';
  }

  if (to.path == '/' && appStore?.value) {
    appStore.value.setBreadcrumbs([{ title: getTitle('dashboard'), href: '/' }]);
  }

  document.title = getTitle(to.meta.title || 'home') + ' | ' + TenantConfig.tenant_name;

  if (appStore?.value && to?.path === from?.path && to?.fullPath !== from?.fullPath) {
    appStore.value.setUpdateUrlParams(to.query);
  }
});

export default router;
